import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
  Alert,
  Pagination,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ContactPhone as ContactIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// Stałe dla etapów lejka sprzedażowego
const LEAD_STAGES = {
  new: 'Nowy',
  qualified: 'Kwalifikowany',
  offer_sent: 'Oferta wysłana',
  negotiation: 'Negocjacje',
  closed_won: 'Zamknięty - Sukces',
  closed_lost: 'Zamknięty - Porażka'
};

// Stałe dla źródeł leadów
const LEAD_SOURCES = {
  website: 'Strona WWW',
  phone: 'Telefon',
  trade_show: 'Targi',
  referral: 'Polecenie',
  social_media: 'Social Media',
  campaign: 'Kampania marketingowa',
  walk_in: 'Wizyta w sklepie',
  other: 'Inne'
};

// Stałe dla filtrów okresów
const CONTACT_PERIODS = {
  last_3_days: 'Ostatnie 3 dni',
  last_7_days: 'Ostatnie 7 dni',
  last_14_days: 'Ostatnie 14 dni',
  no_contact_3: 'Brak kontaktu > 3 dni',
  no_contact_7: 'Brak kontaktu > 7 dni'
};

const Leads = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Stan podstawowy
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  
  // Filtry i wyszukiwanie
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: 'all',
    stage: 'all',
    source: 'all',
    contactPeriod: 'all'
  });
  
  // Sortowanie
  const [sortBy, setSortBy] = useState('lastContactDate');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Dialogi
  const [openAddLead, setOpenAddLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stageMenu, setStageMenu] = useState(null);
  const [selectedLeadForStage, setSelectedLeadForStage] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Nowy lead - formularz
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    email: '',
    source: 'website',
    assignedTo: isAdmin ? '' : currentUser?.uid || '',
    initialNotes: '',
    expectedValue: '',
    expectedCloseDate: ''
  });

  useEffect(() => {
    fetchLeads();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [currentUser, isAdmin, filters, sortBy, sortOrder, page]);

  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const leadsRef = collection(db, 'leads');
      let leadsQuery;

      // Budowanie zapytania w zależności od uprawnień
      if (isAdmin) {
        if (filters.assignedTo !== 'all') {
          leadsQuery = query(
            leadsRef,
            where('assigned_to', '==', filters.assignedTo),
            orderBy(sortBy, sortOrder),
            limit(20)
          );
        } else {
          leadsQuery = query(
            leadsRef,
            orderBy(sortBy, sortOrder),
            limit(20)
          );
        }
      } else {
        // Sprzedawca widzi tylko swoje leady
        leadsQuery = query(
          leadsRef,
          where('assigned_to', '==', currentUser.uid),
          orderBy(sortBy, sortOrder),
          limit(20)
        );
      }

      const leadsSnapshot = await getDocs(leadsQuery);
      let leadsList = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate(),
        lastContactDate: doc.data().lastContactDate?.toDate(),
        expectedCloseDate: doc.data().expectedCloseDate?.toDate()
      }));

      // Zastosuj filtry
      leadsList = applyLeadFilters(leadsList);
      
      setLeads(leadsList);
      setTotalLeads(leadsList.length);
      setTotalPages(Math.ceil(leadsList.length / 20));
    } catch (error) {
      console.error('Błąd podczas pobierania leadów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania danych leadów',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyLeadFilters = (leadsList) => {
    return leadsList.filter(lead => {
      // Filtr wyszukiwania
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const fullName = `${lead.firstName} ${lead.lastName}`.toLowerCase();
        const email = (lead.email || '').toLowerCase();
        const phone = (lead.phone || '').toLowerCase();
        
        if (!fullName.includes(searchTerm) && 
            !email.includes(searchTerm) && 
            !phone.includes(searchTerm)) {
          return false;
        }
      }

      // Filtr etapu
      if (filters.stage !== 'all' && lead.currentStage !== filters.stage) {
        return false;
      }

      // Filtr źródła
      if (filters.source !== 'all' && lead.source !== filters.source) {
        return false;
      }

      // Filtr okresu ostatniego kontaktu
      if (filters.contactPeriod !== 'all') {
        const now = new Date();
        const contactDate = lead.lastContactDate;
        
        switch (filters.contactPeriod) {
          case 'last_3_days':
            if (!contactDate || (now - contactDate) > 3 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last_7_days':
            if (!contactDate || (now - contactDate) > 7 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last_14_days':
            if (!contactDate || (now - contactDate) > 14 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'no_contact_3':
            if (contactDate && (now - contactDate) <= 3 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'no_contact_7':
            if (contactDate && (now - contactDate) <= 7 * 24 * 60 * 60 * 1000) return false;
            break;
        }
      }

      return true;
    });
  };

  const handleAddLead = async () => {
    try {
      const leadData = {
        ...newLead,
        currentStage: 'new',
        createdAt: new Date(),
        updatedAt: new Date(),
        lastContactDate: new Date(),
        expectedValue: parseFloat(newLead.expectedValue) || 0,
        expectedCloseDate: newLead.expectedCloseDate ? new Date(newLead.expectedCloseDate) : null,
        conversionHistory: [{
          stage: 'new',
          timestamp: new Date(),
          employeeId: newLead.assignedTo
        }]
      };

      await addDoc(collection(db, 'leads'), leadData);

      setSnackbar({
        open: true,
        message: 'Lead został dodany pomyślnie',
        severity: 'success'
      });

      setOpenAddLead(false);
      setNewLead({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        source: 'website',
        assignedTo: isAdmin ? '' : currentUser?.uid || '',
        initialNotes: '',
        expectedValue: '',
        expectedCloseDate: ''
      });

      fetchLeads();
    } catch (error) {
      console.error('Błąd podczas dodawania leada:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas dodawania leada',
        severity: 'error'
      });
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      const leadRef = doc(db, 'leads', leadId);
      const updateData = {
        currentStage: newStage,
        updatedAt: new Date()
      };

      // Jeśli etap to "closed_won", przygotuj dane do konwersji na klienta
      if (newStage === 'closed_won') {
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          // Utwórz nowego klienta
          const clientData = {
            firstName: lead.firstName,
            lastName: lead.lastName,
            phone: lead.phone,
            email: lead.email,
            assignedTo: lead.assignedTo,
            status: 'New',
            preferences: [],
            notes: `Konwertowany z leada. Notatki początkowe: ${lead.initialNotes}`,
            createdAt: new Date(),
            lastContactDate: new Date(),
            lastPurchaseDate: null,
            totalSpent: 0,
            leadId: leadId // Zachowujemy powiązanie z leadem
          };

          await addDoc(collection(db, 'clients'), clientData);
          
          setSnackbar({
            open: true,
            message: 'Lead został przekonwertowany na klienta!',
            severity: 'success'
          });
        }
      }

      await updateDoc(leadRef, updateData);
      
      setStageMenu(null);
      setSelectedLeadForStage(null);
      fetchLeads();
      
    } catch (error) {
      console.error('Błąd podczas zmiany etapu leada:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas zmiany etapu leada',
        severity: 'error'
      });
    }
  };

  const handleViewLead = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : 'Nieznany';
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'new': return 'info';
      case 'qualified': return 'primary';
      case 'offer_sent': return 'warning';
      case 'negotiation': return 'secondary';
      case 'closed_won': return 'success';
      case 'closed_lost': return 'error';
      default: return 'default';
    }
  };

  const getStageProgress = (stage) => {
    const stages = ['new', 'qualified', 'offer_sent', 'negotiation', 'closed_won'];
    const index = stages.indexOf(stage);
    return index >= 0 ? ((index + 1) / stages.length) * 100 : 0;
  };

  const renderFilters = () => (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography>Filtry i wyszukiwanie</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Wyszukaj leada"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              placeholder="Imię, nazwisko, email, telefon..."
            />
          </Grid>

          {isAdmin && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Przypisany do</InputLabel>
                <Select
                  value={filters.assignedTo}
                  onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                  label="Przypisany do"
                >
                  <MenuItem value="all">Wszyscy pracownicy</MenuItem>
                  {employees.map(employee => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Etap</InputLabel>
              <Select
                value={filters.stage}
                onChange={(e) => setFilters({...filters, stage: e.target.value})}
                label="Etap"
              >
                <MenuItem value="all">Wszystkie etapy</MenuItem>
                {Object.entries(LEAD_STAGES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Źródło</InputLabel>
              <Select
                value={filters.source}
                onChange={(e) => setFilters({...filters, source: e.target.value})}
                label="Źródło"
              >
                <MenuItem value="all">Wszystkie źródła</MenuItem>
                {Object.entries(LEAD_SOURCES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Ostatni kontakt</InputLabel>
              <Select
                value={filters.contactPeriod}
                onChange={(e) => setFilters({...filters, contactPeriod: e.target.value})}
                label="Ostatni kontakt"
              >
                <MenuItem value="all">Wszystkie okresy</MenuItem>
                {Object.entries(CONTACT_PERIODS).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddLead(true)}
              fullWidth
            >
              Dodaj
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Zarządzanie leadami
      </Typography>

      {renderFilters()}

      {/* Statystyki */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Łączna liczba leadów
              </Typography>
              <Typography variant="h4">
                {totalLeads}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nowe
              </Typography>
              <Typography variant="h4">
                {leads.filter(l => l.currentStage === 'new').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Kwalifikowane
              </Typography>
              <Typography variant="h4">
                {leads.filter(l => l.currentStage === 'qualified').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                W negocjacjach
              </Typography>
              <Typography variant="h4">
                {leads.filter(l => l.currentStage === 'negotiation').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Sukces
              </Typography>
              <Typography variant="h4">
                {leads.filter(l => l.currentStage === 'closed_won').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Wartość pipeline
              </Typography>
              <Typography variant="h4">
                {formatPrice(leads.reduce((sum, lead) => {
                  if (['new', 'qualified', 'offer_sent', 'negotiation'].includes(lead.currentStage)) {
                    return sum + (lead.expectedValue || 0);
                  }
                  return sum;
                }, 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela leadów */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imię i Nazwisko</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Źródło</TableCell>
              {isAdmin && <TableCell>Przypisany do</TableCell>}
              <TableCell>Etap</TableCell>
              <TableCell>Ostatni kontakt</TableCell>
              <TableCell>Przewidywana wartość</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Brak leadów spełniających kryteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {`${lead.firstName} ${lead.lastName}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.phone}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {LEAD_SOURCES[lead.source] || lead.source}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>{getEmployeeName(lead.assignedTo)}</TableCell>
                  )}
                  <TableCell>
                    <Chip 
                      label={LEAD_STAGES[lead.currentStage] || lead.currentStage} 
                      color={getStageColor(lead.currentStage)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {lead.lastContactDate ? 
                      format(lead.lastContactDate, 'dd.MM.yyyy', { locale: pl }) : 
                      'Brak kontaktu'
                    }
                  </TableCell>
                  <TableCell>
                    <strong>{formatPrice(lead.expectedValue || 0)}</strong>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Zobacz szczegóły">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewLead(lead.id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dodaj kontakt">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/leads/${lead.id}/contact`)}
                        >
                          <ContactIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zmień etap">
                        <IconButton 
                          size="small"
                          onClick={(event) => {
                            setStageMenu(event.currentTarget);
                            setSelectedLeadForStage(lead);
                          }}
                        >
                          <TrendingUpIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginacja */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Menu zmiany etapu */}
      <Menu
        anchorEl={stageMenu}
        open={Boolean(stageMenu)}
        onClose={() => {
          setStageMenu(null);
          setSelectedLeadForStage(null);
        }}
      >
        {Object.entries(LEAD_STAGES).map(([key, value]) => (
          <MenuItem
            key={key}
            onClick={() => handleStageChange(selectedLeadForStage?.id, key)}
            disabled={selectedLeadForStage?.currentStage === key}
          >
            <ListItemIcon>
              {key === 'closed_won' ? <CheckCircleIcon /> : 
               key === 'closed_lost' ? <CancelIcon /> : 
               <TrendingUpIcon />}
            </ListItemIcon>
            <ListItemText primary={value} />
          </MenuItem>
        ))}
      </Menu>

      {/* Dialog dodawania leada */}
      <Dialog open={openAddLead} onClose={() => setOpenAddLead(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dodaj nowego leada</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Imię"
                value={newLead.firstName}
                onChange={(e) => setNewLead({...newLead, firstName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwisko"
                value={newLead.lastName}
                onChange={(e) => setNewLead({...newLead, lastName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="Nazwa firmy"
                value={newLead.companyName}
                onChange={(e) => setNewLead({...newLead, companyName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({...newLead, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={newLead.phone}
                onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Źródło</InputLabel>
                <Select
                  value={newLead.source}
                  onChange={(e) => setNewLead({...newLead, source: e.target.value})}
                  label="Źródło"
                >
                  {Object.entries(LEAD_SOURCES).map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Przypisz do opiekuna</InputLabel>
                  <Select
                    value={newLead.assignedTo}
                    onChange={(e) => setNewLead({...newLead, assignedTo: e.target.value})}
                    label="Przypisz do opiekuna"
                  >
                    {employees.map(employee => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {`${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Przewidywana wartość (PLN)"
                type="number"
                value={newLead.expectedValue}
                onChange={(e) => setNewLead({...newLead, expectedValue: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Przewidywana data zamknięcia"
                type="date"
                value={newLead.expectedCloseDate}
                onChange={(e) => setNewLead({...newLead, expectedCloseDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notatki początkowe"
                multiline
                rows={3}
                value={newLead.initialNotes}
                onChange={(e) => setNewLead({...newLead, initialNotes: e.target.value})}
                placeholder="Dodatkowe informacje o leadzie..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddLead(false)} variant="contained">Anuluj</Button>
          <Button 
            onClick={handleAddLead} 
            variant="contained"
            disabled={!newLead.firstName || !newLead.lastName}
          >
            Dodaj leada
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Leads;
